import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Messages } from '~src/codegen/Localize';
import { IRemoteAgentConnectionStatus } from '~src/settings/bridge-settings/serverApi/requests/GetSiteRemoteAgentsConnectionStatusRequest';
import { IRemoteQueryAgent } from '~src/settings/types/ISiteAdminBridgeSettings';

import { SiteBridgeSettingsForSiteAdminProps } from '../../BridgeSiteSettings';
import { LoadingPoolsSpinner } from '../bridgeMultiPool/LoadingPoolsSpinner';
import { PoolAssignmentOption } from '../types/PoolAssignmentOption';
import { PoolID, SystemDefinedPoolID } from '../types/PoolID';
import { UserDefinedPoolModel } from '../types/PoolModel';
import { createPoolOptionsFromPools, createPoolOptionsFromSystemDefinedPools } from '../utils/PoolModelUtilities';
import { BridgeRemoteAgentsTableRow } from './BridgeRemoteAgentsTableRow';

export const REMOTE_AGENTS_FIELD = 'settings.siteAdminBridgeSettings.remoteAgentSettings.agents';
export const DEFAULT_TEST_ID = '-default';
export const UNASSIGNED_TEST_ID = '-unassigned';
export const EMPTY_TEST_ID = '';

export type BridgeRemoteAgentsSectionBodyProps = Readonly<{
  isDefaultPool: boolean;
  pools: Record<string, UserDefinedPoolModel>;
  connectionStatuses: IRemoteAgentConnectionStatus[];
  siteId: string;
  refreshEdgePools: () => void;
  agents: IRemoteQueryAgent[];
  hasLoadedEdgePools: boolean;
}>;

export function BridgeRemoteAgentsSectionBody(props: BridgeRemoteAgentsSectionBodyProps) {
  // TODO - ndepass 9/20/21 - Story 1297102: [BridgeSiteSettings] Create Multi-Pool table
  // remove formik when edgeMultiPoolEnabled FF is removed
  const formik = useFormikContext<SiteBridgeSettingsForSiteAdminProps>();
  const remoteAgents = formik.values.settings.siteAdminBridgeSettings.remoteAgentSettings.agents;

  const poolOptions: ReadonlyArray<PoolAssignmentOption> = React.useMemo(() => {
    let items: PoolAssignmentOption[] = createPoolOptionsFromPools(props.pools);
    items = items.concat(createPoolOptionsFromSystemDefinedPools());
    return items;
  }, [props.pools]);

  let remoteAgentsInPool = remoteAgents;
  let poolTestId = EMPTY_TEST_ID;
  if (FeatureFlags.edgeMultiPoolEnabled) {
    if(!props.hasLoadedEdgePools) {
      return LoadingPoolsSpinner();
    }
    remoteAgentsInPool = props.agents;
    poolTestId = props.isDefaultPool ? DEFAULT_TEST_ID : UNASSIGNED_TEST_ID;
  }

  if (remoteAgentsInPool.length === 0) {
    return renderNoAgentsInPoolWarning(props.isDefaultPool);
  }

  return (
    <>
      <tbody>
        <FieldArray
          name={REMOTE_AGENTS_FIELD}
          render={(arrayHelpers: FieldArrayRenderProps) => (
            remoteAgentsInPool.map((agent: IRemoteQueryAgent, index: number) => (
              <tr key={index}>
                <BridgeRemoteAgentsTableRow
                  agent={agent}
                  index={index}
                  connectionStatuses={props.connectionStatuses}
                  arrayHelpers={arrayHelpers}
                  poolTestId={poolTestId}
                  isDefaultPool={props.isDefaultPool}
                  formik={formik}
                  poolOptions={poolOptions}
                  siteId={props.siteId}
                  agentPoolId={(props.isDefaultPool ? SystemDefinedPoolID.Default : SystemDefinedPoolID.Unassigned) as PoolID}
                  refreshEdgePools={props.refreshEdgePools}
                />
              </tr>
            ))
          )}
        />
      </tbody>
    </>);
}

function renderNoAgentsInPoolWarning(isDefaultPool: boolean) {
  return (
    <tbody>
      <tr>
        <td colSpan={9}>
          <div className={'tb-data-grid-cell-text tb-data-grid-bridge-center'}>
            {isDefaultPool
              ? Messages.siteRemoteAgentNotFoundWarning_simple()
              : Messages.siteRemoteAgentUnassignedClientsNotFoundWarning()}
          </div>
        </td>
      </tr>
    </tbody>);
}
